import { useEffect, useMemo } from 'react';
import { Controller, useForm, type Resolver } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Switch,
  TextField,
  useMediaQuery,
} from '@mui/material';
import { MdTranslate } from 'react-icons/md';
import { todayIso } from '@utils/format';
import { useActiveLocales } from '@features/locales/hooks';
import type { Locale } from '@features/locales/schema';
import { ApmcScopePicker } from './ApmcScopePicker';
import {
  closureFormSchema,
  type Closure,
  type ClosureDetail,
  type ClosureFormTranslation,
  type ClosureFormValues,
} from '../schema';
import {
  useClosure,
  useCreateClosure,
  useUpdateClosure,
} from '../hooks';

interface Props {
  open: boolean;
  closure: Closure | null;
  onClose: () => void;
}

function buildFormTranslations(
  locales: Locale[],
  existing: ClosureDetail['translations'] | undefined,
): ClosureFormTranslation[] {
  const byCode = new Map(existing?.map((t) => [t.localeCode, t]) ?? []);
  return locales
    .filter((l) => !l.isDefault)
    .map((locale) => ({
      localeCode: locale.code,
      localeName: locale.name,
      name: byCode.get(locale.code)?.name ?? '',
      description: byCode.get(locale.code)?.description ?? '',
    }));
}

function buildEmptyDefaults(
  locales: Locale[],
  existing?: ClosureDetail | null,
): ClosureFormValues {
  return {
    name: existing?.name ?? '',
    description: existing?.description ?? '',
    startDate: existing?.startDate ?? todayIso(),
    endDate: existing?.endDate ?? todayIso(),
    isActive: existing?.isActive ?? true,
    apmcIds: existing?.apmcs.map((a) => a.id) ?? [],
    translations: buildFormTranslations(locales, existing?.translations),
  };
}

export function MarketClosureFormDialog({ open, closure, onClose }: Props) {
  const isEditing = !!closure;
  const createMutation = useCreateClosure();
  const updateMutation = useUpdateClosure();
  const submitting = createMutation.isPending || updateMutation.isPending;

  const { data: locales = [], isLoading: localesLoading } = useActiveLocales();
  const detailQuery = useClosure(closure?.id ?? null);
  const detail = detailQuery.data ?? null;

  const dataReady = !localesLoading && (!isEditing || detailQuery.isSuccess);

  const {
    control,
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<ClosureFormValues>({
    resolver: zodResolver(closureFormSchema) as Resolver<ClosureFormValues>,
    defaultValues: useMemo(() => buildEmptyDefaults([]), []),
    mode: 'onSubmit',
    reValidateMode: 'onBlur',
  });

  useEffect(() => {
    if (!open || !dataReady) return;
    reset(buildEmptyDefaults(locales, isEditing ? detail : null));
  }, [open, dataReady, locales, detail, isEditing, reset]);

  // Full-screen dialog below Tailwind's `sm` breakpoint (640px) so the form +
  // APMC picker are usable on phones. Raw query keeps it decoupled from the
  // MUI theme (the app is otherwise desktop-first with no breakpoint config).
  const isMobile = useMediaQuery('(max-width: 639px)');

  const translations = watch('translations');
  const startDate = watch('startDate');
  // Past dates are barred when ADDING a closure (create mode). When editing an
  // existing — possibly already-past — closure, keep its dates selectable.
  const startMin = isEditing ? undefined : todayIso();

  const onSubmit = handleSubmit(async (values) => {
    // Normalize: empty description → null on the wire.
    const cleaned: ClosureFormValues = {
      ...values,
      description: values.description?.trim() ? values.description : null,
    };
    if (isEditing && closure) {
      await updateMutation.mutateAsync({ id: closure.id, values: cleaned });
    } else {
      await createMutation.mutateAsync(cleaned);
    }
    onClose();
  });

  return (
    <Dialog
      open={open}
      onClose={submitting ? undefined : onClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
    >
      <DialogTitle>
        {isEditing ? 'Edit closure' : 'New market closure'}
      </DialogTitle>
      <DialogContent dividers>
        {!dataReady ? (
          <Box className="flex items-center justify-center py-12">
            <CircularProgress size={28} />
          </Box>
        ) : (
          <form id="closure-form" onSubmit={onSubmit} className="grid gap-4 pt-2">
            <TextField
              label="Name"
              size="small"
              fullWidth
              placeholder="e.g. Republic Day, Warangal Strike, Cyclone closure"
              error={!!errors.name}
              helperText={errors.name?.message ?? ' '}
              {...register('name')}
            />

            <TextField
              label="Description (optional)"
              size="small"
              fullWidth
              multiline
              minRows={2}
              placeholder="Shown on the mobile detail screen. Leave blank for label-only display."
              error={!!errors.description}
              helperText={
                errors.description?.message ??
                'Wrap text in **double asterisks** to render it bold on mobile.'
              }
              {...register('description')}
            />

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <TextField
                label="Start date"
                size="small"
                type="date"
                fullWidth
                error={!!errors.startDate}
                helperText={errors.startDate?.message ?? ' '}
                slotProps={{
                  inputLabel: { shrink: true },
                  htmlInput: { min: startMin },
                }}
                {...register('startDate')}
              />
              <TextField
                label="End date"
                size="small"
                type="date"
                fullWidth
                error={!!errors.endDate}
                helperText={
                  errors.endDate?.message ?? 'Same as start = single-day closure'
                }
                slotProps={{
                  inputLabel: { shrink: true },
                  // End can't precede start; in create mode it also can't be in the past.
                  htmlInput: { min: startDate || startMin },
                }}
                {...register('endDate')}
              />
            </div>

            <Controller
              control={control}
              name="isActive"
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Switch
                      checked={field.value}
                      onChange={(e) => field.onChange(e.target.checked)}
                      size="small"
                    />
                  }
                  label={
                    <span className="text-[13px]">
                      Active{' '}
                      <span className="text-foreground-muted">
                        — visible to the mobile app
                      </span>
                    </span>
                  }
                />
              )}
            />

            <Controller
              control={control}
              name="apmcIds"
              render={({ field, fieldState }) => (
                <ApmcScopePicker
                  value={field.value}
                  onChange={field.onChange}
                  error={fieldState.error?.message}
                />
              )}
            />

            {translations.length > 0 && (
              <div className="grid gap-4">
                <div className="-mb-2 mt-2 flex items-center gap-2 text-[12px] font-semibold uppercase tracking-wider text-foreground-muted">
                  <MdTranslate size={14} />
                  Translations{' '}
                  <span className="font-normal normal-case">· optional</span>
                </div>
                {translations.map((t, i) => {
                  const nameErr = errors.translations?.[i]?.name?.message;
                  const descErr = errors.translations?.[i]?.description?.message;
                  return (
                    <div
                      key={t.localeCode}
                      className="grid gap-3 rounded-lg border border-border bg-surface-muted/40 p-3"
                    >
                      <div className="text-[12px] font-semibold text-foreground">
                        {t.localeName}{' '}
                        <span className="font-mono text-[11px] text-foreground-muted">
                          ({t.localeCode})
                        </span>
                      </div>
                      <TextField
                        label={`Name (${t.localeName})`}
                        size="small"
                        fullWidth
                        error={!!nameErr}
                        helperText={nameErr ?? ' '}
                        {...register(`translations.${i}.name` as const)}
                      />
                      <TextField
                        label={`Description (${t.localeName})`}
                        size="small"
                        fullWidth
                        multiline
                        minRows={2}
                        error={!!descErr}
                        helperText={
                          descErr ??
                          'Wrap text in **double asterisks** for bold on mobile.'
                        }
                        {...register(`translations.${i}.description` as const)}
                      />
                    </div>
                  );
                })}
              </div>
            )}
          </form>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          type="submit"
          form="closure-form"
          variant="contained"
          disabled={submitting || !dataReady}
        >
          {submitting ? 'Saving…' : isEditing ? 'Save changes' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
